import { SUPPORTED_LANG } from "@/shared/config/localstorage";
import { getAdminPreviewAsUser } from "@/shared/config/admin";
import { getStoredUtm } from "@/shared/lib/utm";
import { newSessionId, randomHex } from "../lib/ids";
import { appCommit, deviceInfo } from "../lib/device";
import { sanitizePath } from "../lib/sanitize";
import { timeZone, toLocalIso, tzOffsetMin } from "../lib/time";
import { isProduction, TAB_KEY } from "./config";
import type { SessionHeader } from "./types";

/**
 * Сеанс живёт ровно столько, сколько экземпляр страницы: seq и события — в памяти
 * модуля. Переходы внутри SPA сеанс не завершают; перезагрузка, возврат из
 * Google или Stripe начинают новый. «Дублировать вкладку» копирует
 * sessionStorage — храни мы там id сеанса, две вкладки писали бы одни и те же
 * seq, и сервер молча выбросил бы события второй.
 */
export interface LogSession {
    root: string;
    startedMs: number;
    seq: number;
    owner: string | null;
    lastEventMs: number;
}

export interface TabInfo {
    tab_id: string;
    last_session_id?: string;
}

const TAB_ID_RE = /^[0-9a-f]{16}$/;

/** Связка сеансов одной вкладки: tab_id и предыдущий сеанс. */
export const readTab = (): TabInfo => {
    try {
        const raw = sessionStorage.getItem(TAB_KEY);
        const t = raw ? (JSON.parse(raw) as Partial<TabInfo> | null) : null;
        if (t && typeof t.tab_id === "string" && TAB_ID_RE.test(t.tab_id)) {
            return {
                tab_id: t.tab_id,
                last_session_id:
                    typeof t.last_session_id === "string" ? t.last_session_id : undefined,
            };
        }
    } catch {
        /* sessionStorage недоступен — вкладка просто без связки */
    }
    return { tab_id: randomHex(8) };
};

export const writeTab = (t: TabInfo): void => {
    try {
        sessionStorage.setItem(TAB_KEY, JSON.stringify(t));
    } catch {
        /* best effort */
    }
};

const referrerOrigin = (): string | undefined => {
    try {
        if (!document.referrer) return undefined;
        const o = new URL(document.referrer).origin;
        return o === window.location.origin ? undefined : o.slice(0, 100);
    } catch {
        return undefined;
    }
};

export const createSession = (
    owner: string | null,
    now: number = Date.now(),
): LogSession => ({
    root: newSessionId(now),
    startedMs: now,
    seq: 0,
    owner,
    lastEventMs: now,
});

export const buildHeader = (
    s: LogSession,
    tab: TabInfo,
    o: { prevSessionId?: string; firstOfPage: boolean },
): SessionHeader => {
    const d = new Date(s.startedMs);
    const seg = window.location.pathname.split("/")[1] || "";
    const preview = getAdminPreviewAsUser();
    return {
        id: s.root,
        started_at_ms: s.startedMs,
        part: 1,
        started_at_utc: d.toISOString(),
        started_at_local: toLocalIso(d),
        tz: timeZone(),
        tz_offset_min: tzOffsetMin(d),
        lang: SUPPORTED_LANG.includes(seg) ? seg : null,
        env: isProduction() ? "production" : "development",
        origin: window.location.origin,
        app_commit: appCommit(),
        device: deviceInfo(),
        is_admin: null,
        preview_as_user: preview,
        user: null,
        tab_id: tab.tab_id,
        prev_session_id: o.prevSessionId,
        entry_path: sanitizePath(window.location.pathname),
        referrer_origin: o.firstOfPage ? referrerOrigin() : undefined,
        utm: getStoredUtm(),
        client_dropped: 0,
    };
};
